/** Long edge target, in pixels (design §5). Never upscales. */
export const COMPRESS_LONG_EDGE_PX = 1600;

/** JPEG encode quality (design §5, ≈200-350 KB per photo at 1600px). */
export const COMPRESS_JPEG_QUALITY = 0.72;

function computeTargetSize(width: number, height: number): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= COMPRESS_LONG_EDGE_PX) {
    // Never upscale an image already smaller than the target.
    return { width, height };
  }
  const ratio = COMPRESS_LONG_EDGE_PX / longEdge;
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to compress photo: canvas.toBlob returned null'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      COMPRESS_JPEG_QUALITY,
    );
  });
}

/**
 * compressPhoto — decodes a captured photo blob and re-encodes it as a
 * compressed JPEG: long edge capped at 1600px (never upscaled), quality
 * 0.72 (design §5, image-compressor.ts).
 *
 * Decoding uses `createImageBitmap(blob, { imageOrientation: 'from-image' })`
 * — the explicit `imageOrientation` option is what makes EXIF orientation
 * preservation reliable across browsers: without it, older engines default
 * to `'none'` (ignoring the EXIF tag, producing a sideways/upside-down
 * photo for the exact rotated-phone shots a curb valet takes). `'from-image'`
 * bakes the correct rotation into the decoded bitmap's pixels, so the
 * compressed output always displays right-side-up regardless of how the
 * device held the camera.
 *
 * Both the decode (createImageBitmap) and the encode (OffscreenCanvas
 * .convertToBlob, or the canvas.toBlob fallback) are asynchronous browser
 * operations that do not block the main thread the way a synchronous
 * pixel-by-pixel loop would — OffscreenCanvas additionally allows this same
 * function to be moved into a Worker later without any API change.
 *
 * Falls back to a regular `<canvas>` + `toBlob` when OffscreenCanvas isn't
 * available (older Safari) — same target size/quality, same orientation
 * handling (the orientation correction already happened at decode time, so
 * the fallback's plain `drawImage` still produces an upright photo).
 */
export async function compressPhoto(input: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(input, { imageOrientation: 'from-image' });

  try {
    const { width, height } = computeTargetSize(bitmap.width, bitmap.height);

    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(width, height);
      const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D | null;
      ctx?.drawImage(bitmap, 0, 0, width, height);
      return await canvas.convertToBlob({ type: 'image/jpeg', quality: COMPRESS_JPEG_QUALITY });
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(bitmap, 0, 0, width, height);
    return await canvasToBlob(canvas);
  } finally {
    bitmap.close();
  }
}
